import { useState, Fragment } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home, TrendingUp, DollarSign, ArrowRight,
  BarChart3, FileText, Shield,
  CheckCircle2, Save,
  Calculator, Star, Play, Quote, PieChart,
  MapPin, Compass, Infinity as InfinityIcon,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/features/auth/AuthContext";
import { FeatureShowcase } from "@/features/landing/FeatureShowcase";
import AuthModal from "@/features/auth/AuthModal";
import heroBg from "@/assets/hero-bg.jpg";
import brandonHero from "@/assets/brandon-turner-hero.jpg";

// ── Dashboard-style preview graphics ────────────────────────────────────────

const ACCENT_COLOR = '#c09d6c';
const ACCENT_LIGHT = '#FDF8F4';
const dotGridStyle: React.CSSProperties = {
  backgroundSize: '16px 16px',
  backgroundImage: 'radial-gradient(circle, #e5e2db 1px, transparent 1px)',
};

const RentalPreviewGraphic = () => (
  <div className="w-[180px] bg-white rounded-md shadow-md border border-gray-200 overflow-hidden transform group-hover:-translate-y-2 transition-transform duration-500">
    <div className="bg-gray-50 border-b border-gray-200 p-1.5 flex gap-1">
      <div className="w-1.5 h-1.5 rounded-full bg-red-300" /><div className="w-1.5 h-1.5 rounded-full bg-yellow-300" /><div className="w-1.5 h-1.5 rounded-full bg-green-300" />
    </div>
    <div className="p-2 space-y-1.5">
      <div className="grid grid-cols-3 gap-1"><div className="h-2 bg-gray-100 rounded" /><div className="h-2 bg-gray-100 rounded" /><div className="h-2 bg-gray-100 rounded" /></div>
      <div className="h-0.5 bg-gray-200" />
      <div className="grid grid-cols-3 gap-1" style={{ backgroundColor: ACCENT_LIGHT }}>
        <div className="h-3 rounded" style={{ backgroundColor: `${ACCENT_COLOR}1A` }} /><div className="h-3 rounded" style={{ backgroundColor: `${ACCENT_COLOR}33` }} /><div className="h-3 rounded" style={{ backgroundColor: `${ACCENT_COLOR}1A` }} />
      </div>
      <div className="grid grid-cols-3 gap-1"><div className="h-2 bg-gray-50 rounded" /><div className="h-2 bg-gray-50 rounded" /><div className="h-2 bg-gray-50 rounded" /></div>
      <div className="grid grid-cols-3 gap-1"><div className="h-2 bg-gray-50 rounded" /><div className="h-2 bg-gray-50 rounded" /><div className="h-2 bg-gray-50 rounded" /></div>
    </div>
  </div>
);

const STRPreviewGraphic = () => {
  const days = [true,true,false,false,true,true,true,true,false,false,true,false,false,false];
  return (
    <div className="w-[160px] bg-white rounded-md shadow-md border border-gray-200 overflow-hidden p-2 transform group-hover:scale-105 transition-transform duration-500">
      <div className="text-[8px] font-bold text-gray-400 mb-2 border-b border-gray-100 pb-1">JULY 2024</div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((booked, i) => (
          <div key={i} className="h-3 bg-gray-50 rounded-sm relative">
            {booked && <div className="absolute inset-1 rounded-full" style={{ backgroundColor: ACCENT_COLOR, opacity: 0.7 }} />}
          </div>
        ))}
      </div>
    </div>
  );
};

const MortgagePreviewGraphic = () => (
  <div className="w-[180px] bg-white rounded shadow-md border border-gray-200 p-3 space-y-2 transform group-hover:scale-110 transition-transform duration-500">
    <div className="space-y-1"><div className="h-1.5 w-1/3 bg-gray-100 rounded" /><div className="h-5 w-full bg-gray-50 border border-gray-100 rounded flex items-center px-1.5 text-[10px] text-gray-400">420,000</div></div>
    <div className="space-y-1"><div className="h-1.5 w-1/4 bg-gray-100 rounded" /><div className="h-5 w-full bg-gray-50 border border-gray-100 rounded flex items-center px-1.5 text-[10px] text-gray-400">7.25%</div></div>
    <div className="pt-2 border-t border-gray-100"><div className="h-8 w-full rounded flex items-center justify-center text-[10px] text-white font-bold" style={{ backgroundColor: ACCENT_COLOR }}>$2,642.12</div></div>
  </div>
);

const BRRRRPreviewGraphic = () => (
  <div className="flex items-center gap-1 transform group-hover:scale-105 transition-transform duration-500">
    {['BUY','REH','RNT','REFI'].map((label, i) => (
      <Fragment key={label}>
        {i > 0 && <div className="h-0.5 w-4 bg-gray-200" />}
        <div className={`${label === 'REFI' ? 'w-10 h-10' : 'w-8 h-8'} rounded-full border-2 flex items-center justify-center text-[8px] font-bold bg-white`}
          style={{ borderColor: label === 'BUY' ? ACCENT_COLOR : '#D1D5DB', color: label === 'BUY' ? ACCENT_COLOR : '#9CA3AF' }}>
          {label}
        </div>
      </Fragment>
    ))}
  </div>
);

const CashFlowPreviewGraphic = () => {
  const bars = [
    { label: 'Income', height: 52, color: ACCENT_COLOR },
    { label: 'Expenses', height: 34, color: '#E5E2DB' },
    { label: 'Surplus', height: 22, color: `${ACCENT_COLOR}80` },
  ];
  return (
    <div className="w-[160px] bg-white rounded-md shadow-md border border-gray-200 p-3 transform group-hover:scale-105 transition-transform duration-500">
      <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-2">Monthly Summary</div>
      <div className="flex items-end gap-3 h-14">
        {bars.map(bar => (
          <div key={bar.label} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full rounded-sm" style={{ height: bar.height, backgroundColor: bar.color }} />
            <div className="text-[6px] text-gray-400 font-medium">{bar.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MarketFinderPreviewGraphic = () => (
  <div className="w-[180px] h-[90px] bg-white rounded-md shadow-md border border-gray-200 overflow-hidden relative transform group-hover:scale-105 transition-transform duration-500">
    <div className="absolute inset-0 bg-blue-50/40" />
    {[
      { top: '20%', left: '22%', size: 7, opacity: 1 },
      { top: '50%', left: '55%', size: 5, opacity: 0.7 },
      { top: '30%', left: '70%', size: 8, opacity: 1 },
      { top: '65%', left: '35%', size: 4, opacity: 0.5 },
      { top: '15%', left: '45%', size: 5, opacity: 0.6 },
      { top: '72%', left: '68%', size: 3, opacity: 0.4 },
    ].map((dot, i) => (
      <div
        key={i}
        className="absolute rounded-full border border-white"
        style={{
          top: dot.top, left: dot.left,
          width: dot.size, height: dot.size,
          backgroundColor: ACCENT_COLOR,
          opacity: dot.opacity,
          boxShadow: `0 0 0 ${dot.size / 2}px ${ACCENT_COLOR}22`,
        }}
      />
    ))}
    <div className="absolute bottom-1.5 left-2 text-[6px] font-bold text-gray-400 uppercase tracking-wider">All U.S. Counties</div>
  </div>
);

const StrategyFinderPreviewGraphic = () => (
  <div className="w-[160px] bg-white rounded-md shadow-md border border-gray-200 p-3 transform group-hover:-translate-y-1 transition-transform duration-500">
    <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-2">Strategy Match</div>
    {[
      { label: 'Long-Term Rental', pct: 88 },
      { label: 'House Hacking', pct: 72 },
      { label: 'BRRRR', pct: 61 },
    ].map(item => (
      <div key={item.label} className="mb-1.5">
        <div className="flex justify-between mb-0.5">
          <span className="text-[6px] text-gray-500">{item.label}</span>
          <span className="text-[6px] font-bold" style={{ color: ACCENT_COLOR }}>{item.pct}%</span>
        </div>
        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${item.pct}%`, backgroundColor: ACCENT_COLOR }} />
        </div>
      </div>
    ))}
  </div>
);

const TOOLS: { slug: string; title: string; category: string; description: string; features: string[]; flagship: boolean; href: string; Preview: React.ComponentType }[] = [
  {
    slug: "rental-analyzer",
    title: "Rental Property Analyzer",
    category: "ACQUISITION",
    description: "Full 7-step investment analysis with cash flow projections, 30-year returns, and AI-powered deal strategy.",
    features: ["Cash-on-Cash ROI", "30-Year Projections", "AI Strategy Writer", "PDF Export"],
    flagship: true,
    href: "/tools/rental-analyzer",
    Preview: RentalPreviewGraphic,
  },
  {
    slug: "str-calculator",
    title: "Short-Term Rental Analyzer",
    category: "ACQUISITION",
    description: "Calculate Airbnb/VRBO profitability factoring in seasonality curves, occupancy rates, and platform fees.",
    features: ["Nightly Rate Modeling", "Occupancy Scenarios", "Monthly Projections"],
    flagship: false,
    href: "/tools/str-calculator",
    Preview: STRPreviewGraphic,
  },
  {
    slug: "mortgage-calculator",
    title: "Mortgage Calculator",
    category: "FINANCING",
    description: "Estimate monthly payments, total interest, and full amortization schedules across any loan scenario.",
    features: ["Monthly Payment", "Total Interest", "Amortization Table"],
    flagship: false,
    href: "/tools/mortgage-calculator",
    Preview: MortgagePreviewGraphic,
  },
  {
    slug: "cashflow-calculator",
    title: "Personal Cash Flow Calculator",
    category: "PLANNING",
    description: "Track your personal income, expenses, and monthly savings to understand your investing capacity and financial runway.",
    features: ["Income Tracking", "Expense Breakdown", "Monthly Surplus"],
    flagship: false,
    href: "/cashflow-calculator",
    Preview: CashFlowPreviewGraphic,
  },
  {
    slug: "market-finder",
    title: "Market Finder Tool",
    category: "MARKET INTEL",
    description: "Compare rent-to-price ratios, home values, and rental rates across every U.S. county to find your best market.",
    features: ["All U.S. Counties", "HUD & Census Data", "Rent-to-Price Ratios"],
    flagship: false,
    href: "/marketfinder",
    Preview: MarketFinderPreviewGraphic,
  },
  {
    slug: "strategy-finder",
    title: "Strategy Finder Quiz",
    category: "PLANNING",
    description: "Answer a few questions about your goals and capital to get a personalized ranking of the best real estate strategies for you.",
    features: ["Personalized Results", "12+ Strategies Ranked", "Actionable Next Steps"],
    flagship: false,
    href: "/strategy-finder",
    Preview: StrategyFinderPreviewGraphic,
  },
];

// Strong ease-out per Emil: front-loads movement so elements snap into place
const EASE_OUT = [0.23, 1, 0.32, 1] as const;

const fadeInUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: EASE_OUT },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.05 } },
};

const features = [
  { icon: Calculator, title: "Step-by-Step Analysis", description: "Walk through every aspect of your deal with our guided 7-step process." },
  { icon: TrendingUp, title: "30-Year Projections", description: "See exactly how your investment performs over time with detailed projections." },
  { icon: Shield, title: "Pro-Grade Accuracy", description: "Built from professional underwriting standards used by seasoned investors." },
  { icon: FileText, title: "Export Pitch Decks", description: "Generate beautiful PDF reports to share with partners and lenders." },
  { icon: DollarSign, title: "True Cash Flow", description: "Get real numbers—monthly cash flow, cash-on-cash ROI, and cap rates." },
  { icon: BarChart3, title: "Save & Compare", description: "Save unlimited analyses and compare deals side-by-side." },
];


const ToolCards = () => (
  <div className="flex flex-wrap justify-center gap-6">
    {TOOLS.map((tool) => {
      const { Preview } = tool;
      return (
        <motion.div key={tool.slug} whileTap={{ scale: 0.98 }} transition={{ duration: 0.12, ease: EASE_OUT }} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
        <Link to={tool.href} className="block group h-full">
          <div className="bg-white rounded-2xl border border-border overflow-hidden flex flex-col h-full transition-all duration-200 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 hover:border-primary/20">
            {/* Preview area */}
            <div className="h-36 border-b border-border relative overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#FAF9F7', ...dotGridStyle }}>
              <Preview />
            </div>
            {/* Content */}
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded" style={{ color: ACCENT_COLOR, backgroundColor: ACCENT_LIGHT }}>
                  {tool.category}
                </span>
              </div>
              <h3 className="font-serif text-base font-semibold mb-2 text-foreground">{tool.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1 line-clamp-2">{tool.description}</p>
              <div className="pt-4 border-t border-border mt-auto">
                <div className="w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90" style={{ backgroundColor: ACCENT_COLOR }}>
                  Use Tool <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-1" />
                </div>
              </div>
            </div>
          </div>
        </Link>
        </motion.div>
      );
    })}
  </div>
);

const Tools = () => {
  const { session } = useAuth();
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"signup" | "login">("signup");

  // Redirect authenticated users to the dashboard Calculators tab
  if (session) {
    return <Navigate to="/calculators" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Curiosity Hook */}
      <div className="bg-primary/10 border-b border-primary/20 py-2.5 text-center">
        <p className="text-sm font-medium text-foreground/80 tracking-wide">
          <span className="text-primary font-semibold">✦</span>{" "}
          The calculator Brandon Turner uses before buying any rental property
        </p>
      </div>

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundColor: '#F5F2EE' }} />
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Content */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE_OUT }} className="text-center lg:text-left">
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.35, ease: EASE_OUT }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-sm font-medium mb-6">
                <Star className="w-4 h-4 text-primary fill-primary" />
                <span className="text-foreground/80">From <span className="text-foreground font-semibold">Brandon Turner</span></span>
              </motion.div>

              <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-serif font-bold leading-[1.15] text-foreground mb-5">
                Underwrite any rental property with 100% confidence in minutes
              </h1>

              <div className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                <p>The calculator used by best-selling real estate author, Brandon Turner. 100% Free.</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6">
                <motion.div whileTap={{ scale: 0.97 }} transition={{ duration: 0.1, ease: EASE_OUT }}>
                  <Button asChild size="lg" className="gap-2 text-base px-6 py-6 gradient-gold text-primary-foreground shadow-gold font-semibold">
                    <Link to="/tools/rental-analyzer">Run Free Analysis <ArrowRight className="w-4 h-4" /></Link>
                  </Button>
                </motion.div>
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /><span>100% Free</span></div>
                <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /><span>No credit card</span></div>
                <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /><span>Professional report export</span></div>
              </div>
            </motion.div>

            {/* Right: Hero Image */}
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.45, delay: 0.08, ease: EASE_OUT }} className="relative">
              <div className="relative max-w-md mx-auto lg:max-w-none">
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border">
                  <img src={brandonHero} alt="Brandon Turner" className="w-full h-auto" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/10 via-transparent to-transparent" />
                </div>
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.35, ease: EASE_OUT }} className="absolute -bottom-6 left-0 right-0 sm:-left-4 sm:right-4 md:-left-8 md:right-8 bg-card rounded-xl p-4 shadow-lg border border-border mx-2 sm:mx-0">
                  <div className="flex gap-3">
                    <Quote className="w-8 h-8 text-primary/30 flex-shrink-0 rotate-180" />
                    <div>
                      <p className="text-sm text-foreground font-medium leading-relaxed mb-2">"Bad math kills investors' dreams. Run the numbers like your future depends on it — because it does!"</p>
                      <p className="text-xs text-muted-foreground font-medium">— Brandon Turner</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Example Deal Result Teaser */}
      <section className="py-14 md:py-20" style={{ backgroundColor: '#F5F2EE' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-2">See What Your Analysis Looks Like</h2>
            <p className="text-muted-foreground">Here's a sample result from our rental analyzer</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            <Card className="border-primary/30 bg-primary/[0.04] shadow-gold/20">
              <CardContent className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground">Monthly Cash Flow</span>
                </div>
                <p className="text-3xl md:text-4xl font-serif font-bold text-primary">$506</p>
                <p className="text-xs text-muted-foreground mt-1">$6,068/year</p>
              </CardContent>
            </Card>
            <Card className="border-border/60">
              <CardContent className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground"><span className="text-sm text-muted-foreground">Cash on Cash Return</span></span>
                </div>
                <p className="text-3xl md:text-4xl font-serif font-bold text-foreground">5.77%</p>
                <p className="text-xs text-muted-foreground mt-1">On $105,200 invested</p>
              </CardContent>
            </Card>
            <Card className="border-border/60">
              <CardContent className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground">Total Cash Needed</span>
                </div>
                <p className="text-3xl md:text-4xl font-serif font-bold text-foreground">$105,200</p>
                <p className="text-xs text-muted-foreground mt-1">All-in: $414,000</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ ease: EASE_OUT, duration: 0.35 }} className="text-center mt-8">
            <motion.div whileTap={{ scale: 0.97 }} transition={{ duration: 0.1, ease: EASE_OUT }} className="inline-block">
              <Button asChild size="lg" className="gap-2 text-base px-8 py-6 gradient-gold text-primary-foreground shadow-gold font-semibold">
                <Link to="/tools/rental-analyzer">Run Your Own Numbers <ArrowRight className="w-4 h-4" /></Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust Stats + Benefits */}
      <section className="py-8 border-y border-border bg-muted">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ animate: { transition: { staggerChildren: 0.08 } } }}
            className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 text-center mb-6 pb-6 border-b border-border"
          >
            {[
              { value: "7", label: "Step Analysis" },
              { value: "2 Min", label: "To Full Analysis" },
              { value: "30 Yrs", label: "Projections Built In" },
              { value: "100%", label: "Free to Start" },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                variants={{ initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: EASE_OUT } } }}
                className="flex items-center gap-8 md:gap-16"
              >
                {i > 0 && <div className="hidden md:block w-px h-10 bg-border" />}
                <div>
                  <div className="text-3xl font-serif font-bold text-foreground">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ animate: { transition: { staggerChildren: 0.06 } } }}
            className="flex flex-wrap items-center justify-center gap-3"
          >
            {[
              { icon: InfinityIcon, label: "Unlimited analyses" },
              { icon: Save, label: "Save & revisit" },
              { icon: FileText, label: "PDF export" },
              { icon: Star, label: "AI deal summary" },
            ].map(({ icon: Icon, label }) => (
              <motion.div
                key={label}
                variants={{ initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0, transition: { duration: 0.25, ease: EASE_OUT } } }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-card text-sm text-foreground/80"
              >
                <Icon className="w-3.5 h-3.5" style={{ color: ACCENT_COLOR }} />
                {label}
              </motion.div>
            ))}
            <motion.div
              variants={{ initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0, transition: { duration: 0.25, ease: EASE_OUT } } }}
              className="text-xs text-muted-foreground"
            >
              No credit card required
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="pt-20 pb-12 md:pt-28 md:pb-14 bg-zinc-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.35, ease: EASE_OUT }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
              The Difference Is <span className="text-primary">in the Details</span>
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">Stop relying on gut feelings. Get the clarity you need to invest with confidence.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.35, ease: EASE_OUT }}>
              <Card className="h-full border-white/10 bg-white/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                      <span className="text-red-400 text-lg">✕</span>
                    </div>
                    <h3 className="font-serif font-semibold text-white">The Old Way</h3>
                  </div>
                  <ul className="space-y-3">
                    {["Guessing at rental income based on listings", "Missing hidden costs like vacancy & repairs", "Spreadsheets that take hours to build", "No clear picture of long-term returns", "Pitching deals with messy, unprofessional docs"].map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-white/55">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-400/50 flex-shrink-0 mt-2" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.35, delay: 0.06, ease: EASE_OUT }}>
              <Card className="h-full border-primary/40 bg-primary/10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-2xl -mr-16 -mt-16" />
                <CardContent className="p-6 relative">
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-serif font-semibold text-white">With FirstDeal Tools</h3>
                  </div>
                  <ul className="space-y-3">
                    {["Real cash flow numbers based on actual data", "Every expense accounted for automatically", "Complete analysis in under 2 minutes", "30-year projections show exactly what you'll earn", "Professional pitch decks ready to send"].map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-white/90">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* CTA after comparison */}
          <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.35, ease: EASE_OUT }} className="text-center mt-12">
            <motion.div whileTap={{ scale: 0.97 }} transition={{ duration: 0.1, ease: EASE_OUT }} className="inline-block">
              <Button asChild size="lg" className="gap-2 text-base px-8 py-6 gradient-gold text-primary-foreground shadow-gold font-semibold">
                <Link to="/tools/rental-analyzer">Analyze a Property Free <ArrowRight className="w-4 h-4" /></Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Feature Showcase */}
      <div className="-mt-8 md:-mt-14">
        <FeatureShowcase />
      </div>

      {/* Features Grid */}
      <section className="pt-20 pb-10 md:pt-28 md:pb-12 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.35, ease: EASE_OUT }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
              Know the <span className="text-primary">Real Numbers</span> Before You Buy
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">The same underwriting approach that's helped build a portfolio of 14,000+ doors, now available to you.</p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true, margin: "-80px" }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature) => (
              <motion.div key={feature.title} variants={fadeInUp}>
                <Card className="h-full shadow-card hover:shadow-card-hover transition-all duration-200 bg-card">
                  <CardContent className="p-6">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <feature.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-lg font-serif font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA after features grid */}
          <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.35, ease: EASE_OUT }} className="text-center mt-12">
            <motion.div whileTap={{ scale: 0.97 }} transition={{ duration: 0.1, ease: EASE_OUT }} className="inline-block">
              <Button asChild size="lg" className="gap-2 text-base px-8 py-6 gradient-gold text-primary-foreground shadow-gold font-semibold">
                <Link to="/tools/rental-analyzer">Analyze a Property Free <ArrowRight className="w-4 h-4" /></Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Tool Cards */}
      <section className="pt-10 pb-20 md:pt-12 md:pb-28 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.35, ease: EASE_OUT }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">All <span className="text-primary">Tools and Calculators</span></h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">Professional-grade investment calculators, completely free to use.</p>
          </motion.div>
          <ToolCards />
        </div>
      </section>

      {/* Final CTA */}
      <motion.section
        initial={{ opacity: 0, scale: 0.98, y: 16 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.4, ease: EASE_OUT }}
        className="mx-auto max-w-5xl px-4 pb-20"
      >
        <div className="relative overflow-hidden rounded-2xl gradient-gold p-10 md:p-14 text-center shadow-gold">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
          <div className="relative">
            <PieChart className="mx-auto mb-4 h-10 w-10 text-primary-foreground/80" />
            <h2 className="font-serif text-2xl font-bold text-primary-foreground sm:text-3xl">Want Expert Guidance on Your Deals?</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-primary-foreground/80">Join First Deal Mastermind and get 1-on-1 mentorship, live weekly calls, and a proven 12-month roadmap.</p>
            <motion.div whileTap={{ scale: 0.97 }} transition={{ duration: 0.1, ease: EASE_OUT }} className="inline-block mt-6">
              <Button asChild size="lg" className="bg-card text-foreground hover:bg-card/90 font-semibold shadow-lg">
                <a href="https://join.firstdeal.com/main-calendar-app-form/" target="_blank" rel="noopener noreferrer">Apply to First Deal <ArrowRight className="ml-2 h-4 w-4" /></a>
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={setShowAuthModal} mode={authModalMode} />

      {/* Video Modal */}
      <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
        <DialogContent className="max-w-4xl p-0 bg-foreground border-none overflow-hidden">
          <div className="aspect-video flex items-center justify-center bg-muted/20">
            <div className="text-center p-8">
              <Play className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Video coming soon</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tools;
